import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { SurveyorProfile } from '../../types';
import { getSurveyorRank } from '../../utils/surveyorRank';
import { toNumber } from '../../utils/number';

const VER_CONFIG: Record<string, { bg: string; color: string; border: string; icon: string; label: string }> = {
  APPROVED: { bg: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', border: 'var(--sc-success)', icon: '🟢', label: 'Verified' },
  PENDING: { bg: 'var(--sc-warning-bg)', color: 'var(--sc-warning-text)', border: 'var(--sc-warning)', icon: '🟡', label: 'Pending' },
  REJECTED: { bg: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', border: 'var(--sc-error)', icon: '🔴', label: 'Rejected' },
};

const PRESENCE: Record<string, { color: string; label: string }> = {
  online:  { color: 'var(--sc-success)', label: 'Online' },
  away:    { color: 'var(--sc-warning)', label: 'Away' },
  offline: { color: 'var(--sc-error)', label: 'Offline' },
};

export const AdminSurveyors: React.FC = () => {
  const [surveyors, setSurveyors] = useState<SurveyorProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => { loadSurveyors(); }, [filter, search]);

  const loadSurveyors = async () => {
    setIsLoading(true);
    try {
      const statusMap: Record<string, string | undefined> = { PENDING: 'PENDING', APPROVED: 'APPROVED', REJECTED: 'REJECTED' };
      const data = filter === 'INTERVIEW'
        ? await adminService.listInterviewQueue({ search: search || undefined })
        : await adminService.listSurveyors({
            verification_status: statusMap[filter],
            search: search || undefined,
          });
      setSurveyors(data.results ?? []);
      setTotal(data.count ?? 0);
    } catch {} finally {
      setIsLoading(false);
    }
  };

  const tabs = ['All', 'INTERVIEW', 'PENDING', 'APPROVED', 'REJECTED'];

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Surveyors ({total})</h1>
          <p style={s.subtitle}>Manage verifications and view performance.</p>
        </div>
        <input style={s.search} placeholder="Search by name, city…" value={search}
          onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div style={s.tabs}>
        {tabs.map((t) => (
          <button key={t} style={{ ...s.tab, ...(filter === t ? s.active : {}) }} onClick={() => setFilter(t)}>
            {t === 'INTERVIEW' ? 'Interview' : t}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div style={s.center}><div style={s.spinner} /></div>
      ) : surveyors.length === 0 ? (
        <div style={s.empty}>No surveyors found.</div>
      ) : (
        <div style={s.tableWrapper}>
          <div style={s.table}>
            <div style={s.thead}>
              {[{ label: 'Name', flex: 2 }, { label: 'City', flex: 1 }, { label: 'Svcs', flex: 1.8 }, { label: 'Rtng', flex: 1 }, { label: 'Rank', flex: 1 }, { label: 'Jobs', flex: 0.6, align: 'center' as const }, { label: 'Stat', flex: 1 }, { label: '', flex: 0.4, align: 'center' as const }].map((h, i) => (
                <div key={i} style={{ ...s.th, flex: h.flex, textAlign: h.align }}>{h.label}</div>
              ))}
            </div>
            {surveyors.map((sv) => {
              const vc = VER_CONFIG[sv.verification_status] || VER_CONFIG.PENDING;
              const ratingAverage = toNumber(sv.rating_average);
              const rank = getSurveyorRank(ratingAverage, toNumber(sv.total_jobs));
              const isVerified = sv.verification_status === 'APPROVED';
              return (
                <Link to={`/admin/surveyors/${sv.id}`} key={sv.id} style={s.row} className="zs-tr">
                  <div style={{ ...s.td, flex: 2, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {/* Presence dot */}
                      <span
                        title={PRESENCE[sv.online_status ?? 'offline']?.label ?? 'Offline'}
                        style={{ width: 9, height: 9, borderRadius: '50%', background: PRESENCE[sv.online_status ?? 'offline']?.color ?? 'var(--sc-error)', border: '1.5px solid var(--sc-bg-secondary)', boxShadow: '0 1px 3px rgba(0,0,0,0.18)', flexShrink: 0, display: 'inline-block' }}
                      />
                      <span style={{ fontWeight: 700, color: 'var(--sc-text-primary)', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>{(sv.first_name || sv.last_name) ? `${sv.first_name} ${sv.last_name}` : sv.email || 'Unknown User'}</span>
                      {isVerified && (
                        <span style={s.verifiedBadge}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                            <path d="M9 12l2 2 4-4" stroke="var(--sc-success-text)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 2l2.4 3.6L18 4.5l-.9 3.8 3.4 1.7-2.5 2.8.5 3.9-3.7.8L12 21l-2.8-3.5-3.7-.8.5-3.9L3.5 10l3.4-1.7L6 4.5l3.6 1.1L12 2z" fill="var(--sc-success)" opacity="0.15" stroke="var(--sc-success)" strokeWidth="1.5" />
                          </svg>
                          <span>Verified</span>
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sv.phone}</div>
                  </div>
                  <div style={{ ...s.td, flex: 1, color: 'var(--sc-text-secondary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sv.base_city}</div>
                  <div style={{ ...s.td, flex: 1.8, fontSize: 11, color: 'var(--sc-text-secondary)', minWidth: 0, overflow: 'hidden' as const, textOverflow: 'ellipsis' as const, whiteSpace: 'nowrap' as const }}>{(sv.service_types ?? []).slice(0, 2).join(', ')}{(sv.service_types ?? []).length > 2 ? ` +${sv.service_types.length - 2}` : ''}</div>
                  <div style={{ ...s.td, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ color: 'var(--sc-warning)', letterSpacing: 1 }}>{'★'.repeat(Math.round(ratingAverage))}</span>
                      <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sc-text-primary)' }}>{ratingAverage.toFixed(1)}</span>
                    </div>
                  </div>
                  <div style={{ ...s.td, flex: 1, minWidth: 0 }}>
                    <span style={{ ...s.badge, background: rank.bg, color: rank.color }}>{rank.icon} {rank.label}</span>
                  </div>
                  <div style={{ ...s.td, flex: 0.6, minWidth: 0, textAlign: 'center' as const, fontWeight: 600, color: 'var(--sc-text-primary)' }}>{sv.total_jobs}</div>
                  <div style={{ ...s.td, flex: 1, minWidth: 0 }}>
                    <span style={{ ...s.statusBadge, background: vc.bg, color: vc.color, borderColor: vc.border }}>
                      <span style={s.statusIcon}>{vc.icon}</span>
                      {vc.label}
                    </span>
                  </div>
                  <div style={{ ...s.td, flex: 0.4, textAlign: 'center' as const, color: 'var(--sc-text-secondary)' }}>→</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif" },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0 },
  search: { padding: '10px 14px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 14, width: 240, outline: 'none', background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)' },
  tabs: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
  tab: { padding: '8px 16px', border: '1px solid var(--sc-border)', borderRadius: 8, background: 'var(--sc-bg-secondary)', fontSize: 13, cursor: 'pointer', fontWeight: 500, color: 'var(--sc-text-secondary)', transition: 'all 0.2s' },
  active: { background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', border: 'none' },
  tableWrapper: { overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch', borderRadius: 14, boxShadow: 'var(--sc-shadow-md)', marginBottom: 24 },
  table: { background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', overflow: 'hidden', minWidth: 860 },
  thead: { display: 'flex', padding: '10px 20px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)', gap: 12 },
  th: { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const },
  row: { display: 'flex', padding: '13px 20px', borderBottom: '1px solid var(--sc-border)', alignItems: 'center', textDecoration: 'none', gap: 12, transition: 'background 0.2s' },
  td: { flex: 1, fontSize: 13, color: 'var(--sc-text-primary)' },
  badge: { padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, display: 'inline-block' },
  verifiedBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 11, fontWeight: 700, color: 'var(--sc-success-text)',
    background: 'var(--sc-success-bg)',
    border: '1px solid var(--sc-success)',
    borderRadius: 20, padding: '2px 8px 2px 5px',
    boxShadow: '0 1px 3px rgba(16,185,129,0.12)',
    letterSpacing: '0.02em',
  } as React.CSSProperties,
  statusBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    border: '1px solid', letterSpacing: '0.02em',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  } as React.CSSProperties,
  statusIcon: { fontSize: 12, lineHeight: 1, flexShrink: 0 } as React.CSSProperties,
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty: { textAlign: 'center' as const, padding: 60, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
};
